import { useTranslation } from 'react-i18next';
import { Bot, Clock, DollarSign, Gauge, Phone, ChevronRight } from 'lucide-react';
import { fmtCost, fmtDuration } from '../lib/format';
import { translateErrorParts } from '../lib/translate-error';
import { useAgentDashboard } from '../hooks/useAgentDashboard';
import { StatCard } from './dashboard/StatCard';

/** Admin landing dashboard — aggregate call metrics (cards), a list of
 *  synced assistants, and a right split panel with the selected assistant's
 *  per-call breakdown. Pure rendering; data + behaviour live in useAgentDashboard. */
export function AdminAgentDashboard() {
  const { t } = useTranslation();
  const { data: d, isLoading, isError, error, openAssistant } = useAgentDashboard();

  if (isError) {
    const { title, description } = translateErrorParts(t, error);
    return (
      <div className="px-4 py-8 max-w-3xl mx-auto">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <div className="font-medium">{title}</div>
          {description ? <div className="mt-1 opacity-90">{description}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:py-8 space-y-6 max-w-5xl mx-auto">
      <header className="space-y-0.5">
        <h1 className="text-2xl font-semibold">{t('dashboard.adminTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('dashboard.windowHint', { days: d?.windowDays ?? 30 })}
        </p>
      </header>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard
          icon={<Bot className="h-5 w-5" />}
          label={t('dashboard.cards.assistants')}
          value={d ? `${d.syncedAgents}/${d.totalAgents}` : '—'}
          hint={t('dashboard.cards.assistantsHint')}
          loading={isLoading}
        />
        <StatCard
          icon={<Phone className="h-5 w-5" />}
          label={t('dashboard.cards.calls')}
          value={d ? (d.totalCallsCapped ? `${d.totalCalls}+` : String(d.totalCalls)) : '—'}
          loading={isLoading}
        />
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          label={t('dashboard.cards.time')}
          value={d ? fmtDuration(d.totalDurationSec) : '—'}
          loading={isLoading}
        />
        <StatCard
          icon={<DollarSign className="h-5 w-5" />}
          label={t('dashboard.cards.cost')}
          value={d ? fmtCost(d.totalCost) : '—'}
          loading={isLoading}
        />
        <StatCard
          icon={<Gauge className="h-5 w-5" />}
          label={t('dashboard.cards.avg')}
          value={d ? fmtDuration(d.avgDurationSec) : '—'}
          loading={isLoading}
        />
      </div>

      {/* Assistants list — click to open the per-assistant split panel */}
      <section className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('dashboard.assistants.title')}
        </h2>
        {d && d.perAgent.length === 0 ? (
          <div className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
            {t('dashboard.assistants.empty')}
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card divide-y divide-border overflow-hidden">
            {(d?.perAgent ?? []).map((s) => (
              <button
                key={s.externalAgentId}
                type="button"
                onClick={() => openAssistant(s)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
              >
                <Bot className="h-4 w-4 shrink-0 text-primary" />
                <span className="flex-1 min-w-0 truncate text-sm font-medium">{s.name}</span>
                <span className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground tabular-nums">
                  <span>{t('dashboard.assistants.callsN', { count: s.calls })}</span>
                  <span>{fmtDuration(s.durationSec)}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
